import { AccountLimits, AccountStatsDetails, LdsQuotas } from 'features/account/types'
import { FalsyValue } from 'features/common/types'
import { QuotasRequestOptions } from 'features/hubspot/types'
import { EventMetricQuota } from 'features/metrics/types'
import { QuotaState, QuotaTypes } from './types'

export interface QuotaCalculationParams {
  stats?: AccountStatsDetails
  quotas?: AccountLimits
  ldsQuotas?: LdsQuotas[]
}

export const quotaDefinitions: Record<QuotaTypes, (context: QuotaCalculationParams) => QuotaState | FalsyValue> = {
  [QuotaTypes.Maps]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: false,
        current: stats?.all_users_maps_count,
        limit: quotas?.maps_quota,
        quotasRequestType: QuotasRequestOptions.TotalMaps,
        eventMetricQuotaType: EventMetricQuota.TotalMaps
      }
    )
  },
  [QuotaTypes.MapsPublic]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        current: stats?.publicMaps,
        limit: quotas?.mapsPublicQuota,
        quotasRequestType: QuotasRequestOptions.PublicMaps,
        eventMetricQuotaType: EventMetricQuota.MaxPublicMaps
      }
    )
  },
  [QuotaTypes.Geocoding]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        current: stats.geocodings,
        limit: quotas.geocodingQuota,
        period: 'month',
        quotasRequestType: QuotasRequestOptions.Geocode,
        eventMetricQuotaType: EventMetricQuota.Geocoding
      }
    )
  },
  [QuotaTypes.Isolines]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        current: stats.isolines,
        limit: quotas.isolinesQuota,
        period: 'month',
        quotasRequestType: QuotasRequestOptions.Geocode,
        eventMetricQuotaType: EventMetricQuota.Geocoding
      }
    )
  },
  [QuotaTypes.MapLoads]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: true,
        current: stats.map_loads,
        limit: quotas.map_loads_quota
      }
    )
  },
  [QuotaTypes.Tokens]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: true,
        current: stats.tokenCount,
        limit: quotas.maxTokens
      }
    )
  },
  [QuotaTypes.Apps]: ({ quotas, stats }) => {
    return (
      typeof quotas?.max_apps === 'number' &&
      quotas && {
        current: stats?.privateApps,
        limit: quotas?.max_apps,
        quotasRequestType: QuotasRequestOptions.Apps,
        eventMetricQuotaType: EventMetricQuota.TotalApps
      }
    )
  },

  [QuotaTypes.Editors]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        current: stats?.editorUsers,
        limit: quotas?.max_editor_users,
        quotasRequestType: QuotasRequestOptions.EditorUsers,
        eventMetricQuotaType: EventMetricQuota.MaxEditorUsers
      }
    )
  },
  [QuotaTypes.Viewers]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        current: stats?.viewerUsers,
        limit: quotas?.max_viewer_users,
        quotasRequestType: QuotasRequestOptions.ViewerUsers,
        eventMetricQuotaType: EventMetricQuota.MaxViewerUsers
      }
    )
  },
  [QuotaTypes.Workflows]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: false,
        current: stats?.all_users_workflows_count,
        limit: quotas?.workflows_quota,
        quotasRequestType: QuotasRequestOptions.Workflows,
        eventMetricQuotaType: EventMetricQuota.Workflows
      }
    )
  },
  [QuotaTypes.Usage]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: false,
        current: stats?.usageQuota,
        limit: quotas?.usageQuota,
        quotasRequestType: QuotasRequestOptions.Usage,
        eventMetricQuotaType: EventMetricQuota.Usage
      }
    )
  },
  [QuotaTypes.Lds]: ({ stats, quotas }) => {
    return (
      stats &&
      quotas && {
        soft: false,
        current: stats?.lds,
        limit: quotas?.ldsQuota,
        quotasRequestType: QuotasRequestOptions.Lds,
        eventMetricQuotaType: EventMetricQuota.Lds
      }
    )
  },
  [QuotaTypes.Basemaps]: ({ quotas }) => {
    return (
      quotas && {
        soft: false,
        limit: quotas?.maxCustomBasemaps
      }
    )
  }
}
